import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ExternalLink, Check, X, Mail, Calendar, Globe, Tag, Package, Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { ReviewSection } from './reviews/ReviewSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_KEYS = ['webspace','domains','vserver','dedi','discordbots','musicbots','licenses','teamspeak','gameserver'];

function ProviderAvatar({ name, logo_url, size = 'lg' }) {
  const [imgErr, setImgErr] = useState(false);
  const initials = name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  const cls = size === 'lg'
    ? 'w-16 h-16 text-xl rounded-2xl'
    : 'w-10 h-10 text-sm rounded-xl';
  if (logo_url && !imgErr) {
    return (
      <img src={logo_url} alt={`${name} Logo`} onError={() => setImgErr(true)}
        className={`${cls} object-contain bg-white/5 border border-slate-700 flex-shrink-0`} />
    );
  }
  return (
    <div className={`${cls} flex items-center justify-center font-bold text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 flex-shrink-0`}>
      {initials}
    </div>
  );
}

export default function ProviderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [provider, setProvider] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page,    setPage]    = useState(1);
  const [perPage, setPerPage] = useState(10);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, prodRes] = await Promise.all([
          axios.get(`${API}/hostings/${id}`),
          axios.get(`${API}/hostings/${id}/products`),
        ]);
        setProvider(pRes.data);
        setProducts(prodRes.data);
      } catch {
        setError(t.provider.notFound);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, t]);

  const activeFeatures  = provider ? FEATURE_KEYS.filter(k => provider[k] === 1) : [];
  const totalPages      = Math.max(1, Math.ceil(products.length / perPage));
  const safePage        = Math.min(page, totalPages);
  const visibleProducts = products.slice((safePage - 1) * perPage, safePage * perPage);

  return (
    <div className="min-h-screen bg-[#020817] flex flex-col">
      <Navbar />
      <main className="flex-1 py-12 px-4 md:px-8 max-w-7xl mx-auto w-full">

        {/* Back */}
        <button onClick={() => navigate('/#vergleich')} data-testid="back-to-table"
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-cyan-400 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> {t.provider.backToOverview}
        </button>

        {loading ? (
          <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : error ? (
          <div className="text-center py-32 text-slate-400">{error}</div>
        ) : provider ? (
          <div className="space-y-8">

            {/* Header card */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-8">
              <div className="flex flex-col sm:flex-row items-start gap-6">
                <ProviderAvatar name={provider.name} logo_url={provider.logo_url} />
                <div className="flex-1 min-w-0">
                  <h1 className="text-3xl font-bold text-white" data-testid="provider-detail-name">{provider.name}</h1>
                  {provider.tagline && (
                    <p className="text-slate-400 mt-1 text-base" data-testid="provider-detail-tagline">{provider.tagline}</p>
                  )}
                  {/* Meta row */}
                  <div className="flex flex-wrap items-center gap-4 mt-4">
                    {provider.url && (
                      <a href={provider.url} target="_blank" rel="noopener noreferrer" data-testid="provider-detail-website"
                        className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300 transition-colors">
                        <Globe className="w-4 h-4" /> {provider.url.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                    {provider.founded_year && (
                      <span className="flex items-center gap-1.5 text-sm text-slate-400">
                        <Calendar className="w-4 h-4 text-slate-500" /> {t.provider.since} {provider.founded_year}
                      </span>
                    )}
                    {provider.contact_email && (
                      <a href={`mailto:${provider.contact_email}`} data-testid="provider-detail-email"
                        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-400 transition-colors">
                        <Mail className="w-4 h-4 text-slate-500" /> {provider.contact_email}
                      </a>
                    )}
                  </div>
                </div>

                {provider.url && (
                  <a href={provider.url} target="_blank" rel="noopener noreferrer" data-testid="provider-detail-visit-btn"
                    className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-xl hover:bg-cyan-400 transition-colors text-sm">
                    {t.provider.visitWebsite} <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>

              {provider.description && (
                <div className="mt-6 pt-6 border-t border-slate-800">
                  <p className="text-slate-300 leading-relaxed text-sm" data-testid="provider-detail-description">
                    {provider.description}
                  </p>
                </div>
              )}
            </div>

            {/* Services */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-6">
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">{t.provider.services}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {FEATURE_KEYS.map(k => (
                  <div key={k} data-testid={`detail-feature-${k}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm ${
                      provider[k] === 1
                        ? 'border-green-500/30 bg-green-500/5 text-slate-200'
                        : 'border-slate-800 bg-transparent text-slate-600'
                    }`}>
                    {provider[k] === 1
                      ? <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                      : <X className="w-3.5 h-3.5 text-slate-700 flex-shrink-0" />}
                    <span>{t.features[k] || k}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Products */}
            {products.length > 0 && (
              <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-6" data-testid="provider-products-section">
                <div className="flex items-center gap-2 mb-5">
                  <Package className="w-4 h-4 text-cyan-400" />
                  <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                    {t.provider.productsTitle}
                  </h2>
                  <span className="ml-1 px-1.5 py-0.5 bg-slate-800 rounded text-xs text-slate-500">{products.length}</span>
                </div>

                <div className="overflow-hidden rounded-xl border border-slate-800">
                  <table className="w-full" data-testid="products-table">
                    <thead>
                      <tr className="border-b border-slate-800 bg-slate-900/40">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t.provider.thProduct}</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">{t.provider.thCategory}</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t.provider.thPrice}</th>
                        <th className="px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleProducts.map((p, i) => (
                        <tr key={p.id} data-testid={`product-row-${i}`}
                          onClick={() => navigate(`/anbieter/${id}/produkt/${p.id}`)}
                          className="border-b border-slate-800/60 last:border-0 hover:bg-cyan-500/[0.04] transition-colors group cursor-pointer">
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white group-hover:text-cyan-400 transition-colors text-sm">
                                {p.name}
                              </span>
                              {/* Tooltip */}
                              <span className="relative flex-shrink-0 hidden sm:inline-flex">
                                <ChevronRight className="w-3.5 h-3.5 text-slate-700 group-hover:text-cyan-500 transition-colors" />
                                <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 rounded-md bg-slate-800 border border-slate-700 text-xs text-slate-200 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg z-50">
                                  {t.table.productRowTooltip}
                                </span>
                              </span>
                            </div>
                            {p.description && (
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{p.description}</p>
                            )}
                            {/* Compact spec chips */}
                            {(p.cpu_cores || p.ram_size || p.storage_size || p.bandwidth) && (
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {p.cpu_cores && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-800 text-slate-400 border border-slate-700/60">{p.cpu_cores}</span>}
                                {p.ram_size  && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-800 text-slate-400 border border-slate-700/60">{p.ram_size} RAM</span>}
                                {p.storage_size && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-800 text-slate-400 border border-slate-700/60">{p.storage_size} {p.storage_type || ''}</span>}
                                {p.bandwidth && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-800 text-slate-400 border border-slate-700/60">{p.bandwidth}</span>}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3.5 hidden sm:table-cell">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-800 text-slate-400 border border-slate-700">
                              <Tag className="w-2.5 h-2.5" />
                              {t.categories[p.category] || p.category}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <span className="font-semibold text-cyan-400 text-sm">
                              {p.price > 0 ? `${Number(p.price).toFixed(2)} €` : t.provider.free}
                            </span>
                            <span className="text-xs text-slate-500 ml-1">{t.provider.period[p.price_period] || ''}</span>
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 transition-colors ml-auto" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-3 text-slate-400">
                      <span>
                        {Math.min((safePage - 1) * perPage + 1, products.length)}–{Math.min(safePage * perPage, products.length)} von {products.length} Produkten
                      </span>
                      <select
                        value={perPage}
                        onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                        className="bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-2 py-1 text-xs cursor-pointer hover:border-slate-600 transition-colors">
                        {[10, 25, 50].map(n => (
                          <option key={n} value={n}>{n} pro Seite</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={safePage === 1}
                        className="p-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                        .reduce((acc, n, i, arr) => {
                          if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
                          acc.push(n);
                          return acc;
                        }, [])
                        .map((n, i) =>
                          n === '…' ? (
                            <span key={`gap-${i}`} className="px-1 text-slate-600">…</span>
                          ) : (
                            <button key={n}
                              onClick={() => setPage(n)}
                              className={`min-w-[28px] h-7 rounded-lg text-xs font-medium border transition-colors ${
                                safePage === n
                                  ? 'bg-cyan-500 border-cyan-500 text-slate-950'
                                  : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'
                              }`}>
                              {n}
                            </button>
                          )
                        )}
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={safePage === totalPages}
                        className="p-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
    {/* ReviewSection */}
            <ReviewSection hostingId={id} />
          </div>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
